import { enrichChatWithReplyQuote, formatMessagePreviewText } from "./messageReplyUtils";

const uid = (value) => (value == null ? "" : String(value));

export { uid };

export const isIncomingMessage = (chat, openChatUserUid, userUid) => {
  if (chat.isOwned === true) return false;
  if (chat.isOwned === false) return true;
  if (chat.status === "Receive") return true;
  if (chat.status === "Send") return false;

  if (chat.senderUid && chat.receiverUid) {
    return (
      uid(chat.senderUid) === uid(openChatUserUid) &&
      uid(chat.receiverUid) === uid(userUid)
    );
  }

  return false;
};

export const getUnreadIncomingMessages = (chatList, openChatUserUid, userUid) => {
  if (!Array.isArray(chatList) || !openChatUserUid || !userUid) return [];

  const unread = [];
  for (const group of chatList) {
    for (const chat of group?.chats || []) {
      if (chat.isRead === true || chat.isRead === "true") continue;
      if (isIncomingMessage(chat, openChatUserUid, userUid)) {
        unread.push(chat);
      }
    }
  }
  return unread;
};

export const consolidateChatListByDate = (chatList) => {
  if (!Array.isArray(chatList)) return [];

  const byDate = new Map();

  for (const group of chatList) {
    if (!group?.msgDate) continue;

    const dateKey = String(group.msgDate);
    const chats = Array.isArray(group.chats) ? group.chats : [];

    if (!byDate.has(dateKey)) {
      byDate.set(dateKey, { ...group, chats: [...chats] });
      continue;
    }

    const existing = byDate.get(dateKey);
    const seenIds = new Set(existing.chats.map((c) => String(c.chatID)));

    for (const chat of chats) {
      const id = String(chat.chatID);
      if (!seenIds.has(id)) {
        existing.chats.push(chat);
        seenIds.add(id);
      }
    }
  }

  return Array.from(byDate.values()).map((group) => ({
    ...group,
    chats: [...group.chats].sort(
      (a, b) => new Date(a.time) - new Date(b.time)
    ),
  }));
};

export const normalizeChatList = (chatList, partnerUid, userUid) => {
  if (!Array.isArray(chatList)) return [];

  const normalized = chatList.map((group) => ({
    ...group,
    chats: (group?.chats || []).map((chat) => {
      let isOwned;
      if (chat.isOwned === true) {
        isOwned = true;
      } else if (chat.isOwned === false) {
        isOwned = false;
      } else if (chat.senderUid && userUid) {
        isOwned = uid(chat.senderUid) === uid(userUid);
      } else if (chat.status === "Receive") {
        isOwned = false;
      } else {
        isOwned = false;
      }

      return {
        ...chat,
        isOwned,
        senderUid: chat.senderUid || (isOwned ? userUid : partnerUid),
        receiverUid: chat.receiverUid || (isOwned ? partnerUid : userUid),
      };
    }),
  }));

  return consolidateChatListByDate(normalized);
};

export const getUnreadSignature = (unreadMessages) =>
  unreadMessages
    .map((m) => m.chatID)
    .filter((id) => id != null)
    .map((id) => String(id))
    .sort()
    .join(",");

const processedBatches = new Map();
const inFlightKeys = new Set();
const manuallyUnreadChats = new Set();

const manualUnreadKey = (partnerUid, chatId) =>
  `${uid(partnerUid)}:${String(chatId)}`;

export const markChatManuallyUnread = (partnerUid, chatId) => {
  if (partnerUid && chatId != null) {
    manuallyUnreadChats.add(manualUnreadKey(partnerUid, chatId));
  }
};

export const clearChatManuallyUnread = (partnerUid, chatId) => {
  if (partnerUid && chatId != null) {
    manuallyUnreadChats.delete(manualUnreadKey(partnerUid, chatId));
  }
};

export const isChatManuallyUnread = (partnerUid, chatId) =>
  partnerUid != null &&
  chatId != null &&
  manuallyUnreadChats.has(manualUnreadKey(partnerUid, chatId));

export const clearManualUnreadForPartner = (partnerUid) => {
  if (!partnerUid) return;
  const prefix = `${uid(partnerUid)}:`;
  for (const key of manuallyUnreadChats) {
    if (key.startsWith(prefix)) manuallyUnreadChats.delete(key);
  }
};

export const filterAutoReadableMessages = (unreadMessages, partnerUid) => {
  if (!Array.isArray(unreadMessages)) return [];
  return unreadMessages.filter(
    (m) => !isChatManuallyUnread(partnerUid, m.chatID)
  );
};

export const shouldProcessUnreadBatch = (userId, signature) => {
  if (!signature) return false;
  return processedBatches.get(uid(userId)) !== signature;
};

export const markUnreadBatchProcessed = (userId, signature) => {
  if (userId && signature) {
    processedBatches.set(uid(userId), signature);
  }
};

export const clearProcessedUnreadBatch = (userId) => {
  if (userId) {
    processedBatches.delete(uid(userId));
  }
};

export const acquireReadLock = (key) => {
  if (inFlightKeys.has(key)) return false;
  inFlightKeys.add(key);
  return true;
};

export const releaseReadLock = (key) => {
  inFlightKeys.delete(key);
};

export const toReadReceiptPayload = (messages) => {
  if (messages == null) return [];

  if (messages.responseData != null) {
    return toReadReceiptPayload(messages.responseData);
  }

  const raw = Array.isArray(messages) ? messages : [messages];

  return raw.flatMap((item) => {
    if (item == null) return [];

    if (typeof item === "number" || typeof item === "string") {
      return [{ chatID: item }];
    }

    if (item.chatID != null) {
      return [item];
    }

    return [];
  });
};

export const isReadReceiptRelevant = (item, partnerUid, userUid) => {
  if (!partnerUid || !userUid) return false;

  const me = uid(userUid);
  const partner = uid(partnerUid);

  if (item?.chatID == null) return false;

  if (item.senderUid != null && item.receiverUid != null) {
    const sender = uid(item.senderUid);
    const receiver = uid(item.receiverUid);

    if (sender === me && receiver === partner) return true;
    if (receiver === me && sender === partner) return true;
    return false;
  }

  return true;
};

export const isHubReceiptForMySentMessage = (hubItem, userUid, partnerUid) => {
  if (!hubItem || !userUid || !partnerUid) return false;
  if (hubItem.senderUid == null || hubItem.receiverUid == null) return false;

  const me = uid(userUid);
  const partner = uid(partnerUid);
  const sender = uid(hubItem.senderUid);
  const receiver = uid(hubItem.receiverUid);

  return sender === me && receiver === partner;
};

export const resolveHubReadStatus = (hubItem, defaultRead = true) => {
  if (hubItem?.isRead === false || hubItem?.isRead === "false") return false;
  if (hubItem?.isRead === true || hubItem?.isRead === "true") return true;
  return defaultRead;
};

export const isHubMarkingUnread = (hubData) =>
  toReadReceiptPayload(hubData).some(
    (item) => resolveHubReadStatus(item, true) === false
  );

export const syncPartnerReadCacheFromHub = (hubData, partnerUid) => {
  if (!partnerUid) return;
  if (isHubMarkingUnread(hubData)) {
    clearPartnerSentMessagesReadCache(partnerUid);
  } else {
    cachePartnerSentMessagesRead(partnerUid);
  }
};

export const applyReadReceiptsToChatList = (
  prevList,
  readItems,
  userUid,
  partnerUid
) => {
  if (!Array.isArray(prevList)) return prevList;

  const items = toReadReceiptPayload(readItems);
  const readChatIds = new Set(
    items.map((item) => String(item.chatID)).filter((id) => id !== "undefined")
  );

  if (readChatIds.size === 0) return prevList;

  const hubByChatId = new Map(
    items
      .filter((item) => item?.chatID != null)
      .map((item) => [String(item.chatID), item])
  );

  return prevList.map((group) => {
    if (!group) return group;

    if (Array.isArray(group.chats)) {
      return {
        ...group,
        chats: group.chats.map((chat) => {
          const id = String(chat.chatID);
          if (!readChatIds.has(id)) return chat;

          const hubItem = hubByChatId.get(id);
          const readStatus = resolveHubReadStatus(hubItem, true);

          if (
            hubItem &&
            isHubReceiptForMySentMessage(hubItem, userUid, partnerUid)
          ) {
            return { ...chat, isRead: readStatus };
          }

          if (partnerUid && userUid && isIncomingMessage(chat, partnerUid, userUid)) {
            if (readStatus === false) {
              return { ...chat, isRead: false };
            }
            return chat;
          }

          return { ...chat, isRead: readStatus };
        }),
      };
    }

    if (group.chatID != null && readChatIds.has(String(group.chatID))) {
      const hubItem = hubByChatId.get(String(group.chatID));
      return { ...group, isRead: resolveHubReadStatus(hubItem, true) };
    }

    return group;
  });
};

export const mergeChatListDeleteStatus = (newList, prevList) => {
  if (!Array.isArray(newList)) return newList;
  if (!Array.isArray(prevList) || prevList.length === 0) return newList;

  const prevDeletedById = new Map();
  for (const group of prevList) {
    for (const chat of group?.chats || []) {
      if (chat.chatID != null && chat.isCompleteDeleted === true) {
        prevDeletedById.set(String(chat.chatID), chat);
      }
    }
  }

  if (prevDeletedById.size === 0) return newList;

  const seenIds = new Set();
  const merged = newList.map((group) => ({
    ...group,
    chats: (group.chats || []).map((chat) => {
      const id = String(chat.chatID);
      seenIds.add(id);

      if (chat.isCompleteDeleted === true) return chat;

      const prevDeleted = prevDeletedById.get(id);
      if (prevDeleted) {
        return {
          ...chat,
          isDeleted: true,
          isCompleteDeleted: true,
          msg: prevDeleted.msg || chat.msg,
        };
      }

      return chat;
    }),
  }));

  const missingDeleted = [];
  for (const [id, chat] of prevDeletedById) {
    if (!seenIds.has(id)) {
      missingDeleted.push(chat);
    }
  }

  if (missingDeleted.length === 0) return merged;

  const conversationUids = new Set();
  for (const group of newList) {
    for (const chat of group?.chats || []) {
      if (chat.senderUid) conversationUids.add(uid(chat.senderUid));
      if (chat.receiverUid) conversationUids.add(uid(chat.receiverUid));
    }
  }

  const byDate = new Map(
    merged.map((group) => [String(group.msgDate), { ...group, chats: [...group.chats] }])
  );

  for (const chat of missingDeleted) {
    if (conversationUids.size > 0) {
      const sender = uid(chat.senderUid);
      const receiver = uid(chat.receiverUid);
      if (!conversationUids.has(sender) || !conversationUids.has(receiver)) {
        continue;
      }
    }

    const msgDate = new Date(chat.time || Date.now()).toISOString().slice(0, 10);
    if (!byDate.has(msgDate)) {
      byDate.set(msgDate, { msgDate, chats: [] });
    }
    byDate.get(msgDate).chats.push(chat);
  }

  return consolidateChatListByDate(Array.from(byDate.values()));
};

export const mergeChatListReadStatus = (newList, prevList, userUid, partnerUid) => {
  if (!Array.isArray(newList)) return newList;
  if (!Array.isArray(prevList) || prevList.length === 0) return newList;

  const readSentIds = new Set();
  for (const group of prevList) {
    for (const chat of group?.chats || []) {
      if (chat.isRead !== true || chat.chatID == null) continue;
      if (partnerUid && isIncomingMessage(chat, partnerUid, userUid)) continue;
      readSentIds.add(String(chat.chatID));
    }
  }

  if (readSentIds.size === 0) return newList;

  return newList.map((group) => ({
    ...group,
    chats: (group.chats || []).map((chat) =>
      readSentIds.has(String(chat.chatID)) ? { ...chat, isRead: true } : chat
    ),
  }));
};

export const markAllSentInOpenChatAsRead = (prevList, partnerUid, userUid) => {
  if (!Array.isArray(prevList) || !partnerUid || !userUid) return prevList;

  return prevList.map((group) => ({
    ...group,
    chats: (group.chats || []).map((chat) => {
      if (isIncomingMessage(chat, partnerUid, userUid)) return chat;
      return { ...chat, isRead: true };
    }),
  }));
};

const fullyReadPartners = new Set();

export const cachePartnerSentMessagesRead = (partnerUid) => {
  if (partnerUid) fullyReadPartners.add(uid(partnerUid));
};

export const clearPartnerSentMessagesReadCache = (partnerUid) => {
  if (partnerUid) fullyReadPartners.delete(uid(partnerUid));
};

export const applyUnreadReceiptsToChatList = (
  prevList,
  unreadItems,
  userUid,
  partnerUid
) => {
  if (!Array.isArray(prevList)) return prevList;

  const items = toReadReceiptPayload(unreadItems);
  const unreadChatIds = new Set(
    items.map((item) => String(item.chatID)).filter((id) => id !== "undefined")
  );

  if (unreadChatIds.size === 0) return prevList;

  const hubByChatId = new Map(
    items
      .filter((item) => item?.chatID != null)
      .map((item) => [String(item.chatID), item])
  );

  return prevList.map((group) => {
    if (!group) return group;

    if (Array.isArray(group.chats)) {
      return {
        ...group,
        chats: group.chats.map((chat) => {
          const id = String(chat.chatID);
          if (!unreadChatIds.has(id)) return chat;

          const hubItem = hubByChatId.get(id);

          if (
            hubItem &&
            isHubReceiptForMySentMessage(hubItem, userUid, partnerUid)
          ) {
            return { ...chat, isRead: false };
          }

          if (partnerUid && userUid && isIncomingMessage(chat, partnerUid, userUid)) {
            return { ...chat, isRead: false };
          }

          if (!hubItem) {
            return { ...chat, isRead: false };
          }

          return chat;
        }),
      };
    }

    if (group.chatID != null && unreadChatIds.has(String(group.chatID))) {
      return { ...group, isRead: false };
    }

    return group;
  });
};

export const mergeCachedPartnerReadReceipts = (chatList, partnerUid, userUid) => {
  if (!fullyReadPartners.has(uid(partnerUid))) return chatList;
  return markAllSentInOpenChatAsRead(chatList, partnerUid, userUid);
};

export const resolvePartnerFromReadHub = (hubData, userUid) => {
  if (!userUid) return null;
  const me = uid(userUid);
  const payload = toReadReceiptPayload(hubData);

  for (const item of payload) {
    if (item.senderUid == null || item.receiverUid == null) continue;
    const sender = uid(item.senderUid);
    const receiver = uid(item.receiverUid);
    if (sender === me) return receiver;
    if (receiver === me) return sender;
  }

  const data = Array.isArray(hubData) ? hubData[0] : hubData;
  if (!data || typeof data !== "object") return null;

  const to = uid(data.toUserUid);
  const from = uid(
    data.fromUserUid ?? data.readerUid ?? data.readByUid ?? data.senderUid
  );

  if (from && from !== me) return from;
  if (to && to !== me) return to;
  if (to === me && from) return from;

  return null;
};

export const logReadReceipt = (eventName, hubData, extra = {}) => {
  const userUid = localStorage.getItem("userUid");
  const partner = resolvePartnerFromReadHub(hubData, userUid);
  const items = toReadReceiptPayload(hubData);
  const mySent = items.filter((item) =>
    isHubReceiptForMySentMessage(item, userUid, partner)
  );

  console.warn(`[ReadReceipt] ${eventName}`, {
    hubData,
    userUid,
    openChat: localStorage.getItem("toUserUid"),
    partner,
    mySentChatIds: mySent.map((m) => m.chatID),
    ...extra,
  });
};

export const isBulkReadAllForOpenChat = (hubData, userUid, partnerUid) => {
  if (!partnerUid || !userUid) return false;

  const parsed = toReadReceiptPayload(hubData);
  if (parsed.length > 0) return false;

  const me = uid(userUid);
  const partner = uid(partnerUid);
  const data = Array.isArray(hubData) ? hubData[0] : hubData;

  if (!data || typeof data !== "object") return true;

  const to = uid(data.toUserUid);
  const from = uid(
    data.fromUserUid ?? data.readerUid ?? data.readByUid ?? data.senderUid
  );

  if (to === me || from === partner) return true;

  if (data.msgID != null) {
    const localMsgId = uid(localStorage.getItem("msgId"));
    if (localMsgId && uid(data.msgID) === localMsgId) return true;
  }

  return false;
};

export const isDeleteHubRelevant = (hubMessage, partnerUid, userUid) => {
  if (!hubMessage?.chatID || !userUid) return false;

  if (!partnerUid) {
    const me = uid(userUid);
    return (
      uid(hubMessage.senderUid) === me || uid(hubMessage.receiverUid) === me
    );
  }

  return isReadReceiptRelevant(hubMessage, partnerUid, userUid);
};

export const applyDeleteHubToChatList = (
  prevList,
  hubMessage,
  userUid,
  partnerUid
) => {
  if (!Array.isArray(prevList) || hubMessage?.chatID == null || !userUid) {
    return prevList;
  }

  const me = uid(userUid);
  const chatId = String(hubMessage.chatID);
  const sender = uid(hubMessage.senderUid);
  const receiver = uid(hubMessage.receiverUid);
  const isCompleteDeleted = hubMessage.isCompleteDeleted === true;
  const locallyDeletedId = localStorage.getItem("DeletedChatId");

  if (partnerUid) {
    const partner = uid(partnerUid);
    const inConversation =
      (sender === me && receiver === partner) ||
      (receiver === me && sender === partner);
    if (!inConversation) return prevList;
  } else if (sender !== me && receiver !== me) {
    return prevList;
  }

  let changed = false;

  const next = prevList
    .map((group) => {
      if (!Array.isArray(group?.chats)) return group;

      const nextChats = group.chats.flatMap((chat) => {
        if (String(chat.chatID) !== chatId) return [chat];

        if (!isCompleteDeleted) {
          // Delete for me — only the client that performed the delete should update.
          if (locallyDeletedId == null || String(locallyDeletedId) !== chatId) {
            return [chat];
          }
          changed = true;
          return [];
        }

        const partner = partnerUid ? uid(partnerUid) : null;
        const isSenderView = sender === me;
        const isReceiverView =
          receiver === me && (partner ? sender === partner : sender !== me);

        if (!isSenderView && !isReceiverView) {
          return [chat];
        }

        changed = true;
        return [
          {
            ...chat,
            isDeleted: true,
            isCompleteDeleted: true,
            msg: isSenderView ? hubMessage.sndrSideMsg : hubMessage.rcvrSideMsg,
            isRead: false,
          },
        ];
      });

      if (nextChats.length === 0 && group.chats.length > 0) {
        changed = true;
      }

      return nextChats.length > 0 ? { ...group, chats: nextChats } : null;
    })
    .filter(Boolean);

  if (changed && locallyDeletedId != null && String(locallyDeletedId) === chatId) {
    localStorage.removeItem("DeletedChatId");
  }

  return changed ? consolidateChatListByDate(next) : prevList;
};

export const applyReadAllHubReceipt = ({
  hubData,
  userUid,
  openPartnerUid,
  chatList,
  allUserChats,
}) => {
  const partnerUid =
    resolvePartnerFromReadHub(hubData, userUid) || openPartnerUid;

  if (!partnerUid || !userUid) {
    return { chatList, allUserChats, applied: false };
  }

  cachePartnerSentMessagesRead(partnerUid);

  const payload = toReadReceiptPayload(hubData);
  let nextChatList = chatList;
  let nextAllUserChats = allUserChats;

  const markList = (list) => {
    if (!Array.isArray(list)) return list;
    if (payload.length > 0) {
      const relevant = payload.filter((msg) =>
        isReadReceiptRelevant(msg, partnerUid, userUid)
      );
      if (relevant.length > 0) {
        return applyReadReceiptsToChatList(list, relevant, userUid, partnerUid);
      }
    }
    if (isBulkReadAllForOpenChat(hubData, userUid, partnerUid)) {
      return markAllSentInOpenChatAsRead(list, partnerUid, userUid);
    }
    return markAllSentInOpenChatAsRead(list, partnerUid, userUid);
  };

  if (uid(openPartnerUid) === uid(partnerUid) && Array.isArray(chatList)) {
    nextChatList = markList(chatList);
  }

  if (allUserChats?.[partnerUid]) {
    nextAllUserChats = {
      ...allUserChats,
      [partnerUid]: markList(allUserChats[partnerUid]),
    };
  }

  return {
    chatList: nextChatList,
    allUserChats: nextAllUserChats,
    applied: true,
    partnerUid,
  };
};

export const resolvePartnerFromReplyChat = (message, userUid) => {
  if (!message || !userUid) return null;

  const me = uid(userUid);
  const sender = uid(message.senderUid);
  const receiver = uid(message.receiverUid);

  if (sender === me) return message.receiverUid;
  if (receiver === me) return message.senderUid;
  return null;
};

export const isReplyChatRelevant = (message, userUid) =>
  resolvePartnerFromReplyChat(message, userUid) != null;

export const appendReplyChatToChatList = (
  prevList,
  message,
  userUid,
  partnerName = ""
) => {
  if (!Array.isArray(prevList) || message?.chatID == null) return prevList;

  const chatId = String(message.chatID);
  const msgDate = new Date(message.time || Date.now()).toISOString().slice(0, 10);

  const formatted = {
    ...message,
    isDeleted: message.isDeleted ?? false,
    isCompleteDeleted: message.isCompleteDeleted ?? false,
    isOwned: uid(message.senderUid) === uid(userUid),
    status: uid(message.senderUid) === uid(userUid) ? "Send" : "Receive",
  };

  const enriched = enrichChatWithReplyQuote(
    formatted,
    prevList,
    partnerName,
    "You",
    userUid
  );

  const isOwn = formatted.isOwned === true;
  if (isOwn) {
    const threadMsgId = localStorage.getItem("msgId");
    if (threadMsgId && String(threadMsgId) !== chatId) {
      for (let gi = 0; gi < prevList.length; gi++) {
        const group = prevList[gi];
        const chats = group?.chats || [];
        const staleIdx = chats.findIndex(
          (c) => c.isOwned === true && String(c.chatID) === String(threadMsgId)
        );
        if (staleIdx !== -1) {
          const updated = [...prevList];
          const updatedChats = [...chats];
          updatedChats[staleIdx] = { ...enriched };
          updated[gi] = { ...group, chats: updatedChats };
          return updated;
        }
      }
    }
  }

  const alreadyExists = prevList.some((group) =>
    (group?.chats || []).some((chat) => String(chat.chatID) === chatId)
  );
  if (alreadyExists) return prevList;

  const existingIndex = prevList.findIndex((group) => group.msgDate === msgDate);
  if (existingIndex !== -1) {
    const updated = [...prevList];
    const existingChats = updated[existingIndex].chats || [];
    updated[existingIndex] = {
      ...updated[existingIndex],
      chats: [...existingChats, enriched],
    };
    return updated;
  }

  return [...prevList, { msgDate, chats: [enriched] }];
};

export const getChatUnreadCount = (item) => {
  if (!item) return 0;

  const fromApi =
    Number(
      item.unreadCount ??
        item.UnreadCount ??
        item.chatInfo?.unreadCount ??
        item.chatInfo?.UnreadCount ??
        0
    ) || 0;

  if (fromApi > 0) return fromApi;
  if (item.lastMsgIsRead === false && item.isOwned !== true) return 1;

  return 0;
};

export const sumChatListUnreadCount = (list) => {
  if (!Array.isArray(list)) return 0;
  return list.reduce((total, item) => total + getChatUnreadCount(item), 0);
};

export const applyReplyChatToMessageList = (
  prevList,
  message,
  userUid,
  openPartnerUid
) => {
  if (!Array.isArray(prevList) || !message || !userUid) return prevList;

  const partnerUid = resolvePartnerFromReplyChat(message, userUid);
  if (!partnerUid) return prevList;

  const index = prevList.findIndex(
    (item) => uid(item.chatInfo?.toUserUid) === uid(partnerUid)
  );
  if (index === -1) return prevList;

  const isOwned = uid(message.senderUid) === uid(userUid);
  const isOpenChat = uid(openPartnerUid) === uid(partnerUid);
  const msgTime = message.time || new Date().toISOString();
  const currentUnreadCount = Number(prevList[index].unreadCount ?? 0) || 0;
  const unreadCount = isOwned ? currentUnreadCount : isOpenChat ? 0 : currentUnreadCount + 1;

  const updatedItem = {
    ...prevList[index],
    chatID: message.chatID ?? prevList[index].chatID,
    lastMsg: formatMessagePreviewText(
      message.msg ?? message.lastMsg ?? prevList[index].lastMsg ?? ""
    ),
    lastMsgTime: msgTime,
    lastMsgIsRead: isOwned ? true : isOpenChat,
    unreadCount,
    isOwned,
    chatInfo: prevList[index].chatInfo
      ? { ...prevList[index].chatInfo, unreadCount }
      : prevList[index].chatInfo,
  };

  const next = [...prevList];
  next.splice(index, 1);

  const isPinned = updatedItem.isPined === true;
  let targetIndex;

  if (isPinned) {
    targetIndex = index;
  } else {
    const firstUnpinnedIndex = next.findIndex((item) => item.isPined !== true);
    targetIndex = firstUnpinnedIndex === -1 ? next.length : firstUnpinnedIndex;
  }

  next.splice(targetIndex, 0, updatedItem);
  return next;
};
